import { toJsDate as toDate } from "./timestamps";

/**
 * Estado de cuenta de un residente — fuente de verdad única.
 *
 * El estado NO se almacena en `users.activationStatus` (campo legacy ambiguo)
 * sino que se deriva de campos atómicos del doc:
 *   - authUid                  → tiene cuenta Firebase Auth
 *   - invitedAt                → se generó al menos una invitación
 *   - activationToken          → hay token vigente para activar
 *   - activationTokenExpiresAt → cuándo vence ese token
 *   - activatedAt              → cuándo el residente activó
 *   - archivedAt               → cuándo el admin lo archivó
 *
 * Esta función es **pura** y reversible: dado el mismo doc, siempre devuelve
 * el mismo estado. Cero side-effects, cero queries.
 *
 * Estados posibles:
 *   - "archived"       → admin archivó. Oculto por defecto en listados.
 *   - "active"         → activó y puede loguearse.
 *   - "invited_active" → tiene token vigente, esperando que active.
 *   - "invited_expired"→ tiene token vencido, requiere renovación.
 *   - "created"        → existe pero nunca se le mandó invitación.
 *
 * Compatibilidad legacy: si los campos nuevos no están seteados pero los viejos
 * sí (activationStatus, onboardingCompletedAt), se infiere correctamente.
 */

/**
 * @typedef {Object} AccountStateUser
 * @property {string|null} [authUid]
 * @property {any} [invitedAt]
 * @property {string|null} [activationToken]
 * @property {any} [activationTokenExpiresAt]
 * @property {any} [activationTokenExpires]
 * @property {any} [activatedAt]
 * @property {any} [archivedAt]
 * @property {any} [onboardingCompletedAt]
 * @property {string} [activationStatus]
 * @property {string} [email]
 */

const ACCOUNT_STATES = Object.freeze({
  ARCHIVED: "archived",
  ACTIVE: "active",
  INVITED_ACTIVE: "invited_active",
  INVITED_EXPIRED: "invited_expired",
  CREATED: "created",
});

/**
 * Calcula el estado de cuenta a partir de los campos atómicos del doc users.
 *
 * @param {AccountStateUser} user
 * @param {Date} [now] - Fecha de referencia (default: Date.now). Útil para tests.
 * @returns {"archived"|"active"|"invited_active"|"invited_expired"|"created"}
 */
export function computeAccountState(user, now = new Date()) {
  if (!user || typeof user !== "object") return ACCOUNT_STATES.CREATED;

  if (toDate(user.archivedAt)) return ACCOUNT_STATES.ARCHIVED;

  // Activo: tiene cualquier marca explícita de activación. authUid es ideal
  // pero no se requiere (residentes legacy lo tienen como null porque ningún
  // flujo viejo lo escribía). Marcas válidas:
  //   - activatedAt set (flujo nuevo via inviteResident + activate)
  //   - onboardingCompletedAt set (flujo legacy ActivarCuenta)
  //   - activationStatus === "activo" (flujo legacy GestionUsuarios.handleActivateUser
  //     + createResidentDirect que escribía esto al crear el Auth user)
  // Si el residente tiene Firebase Auth (cuenta para loguearse) pero el doc
  // users no se sincronizó con authUid, igual lo consideramos active porque
  // el usuario ya pasó por la activación y SÍ puede loguearse.
  const hasActivatedAt =
    !!toDate(user.activatedAt) || !!toDate(user.onboardingCompletedAt);
  const legacyActivo = user.activationStatus === "activo";
  // Si el doc ya está vinculado a una cuenta Firebase Auth (authUid), el
  // residente pasó la activación y puede loguearse aunque falten las marcas
  // legacy — el login lo backfillea (callable vincularMiCuenta). Esto cumple
  // la intención documentada arriba, que antes no estaba implementada.
  const hasLinkedAuth = !!user.authUid;
  if (hasActivatedAt || legacyActivo || hasLinkedAuth) {
    return ACCOUNT_STATES.ACTIVE;
  }

  // Tiene invitación: chequear vigencia. Desde la Fase 0C el token vive solo
  // en el servidor (activation_tokens) y users guarda `invitePending` + el
  // vencimiento; los docs viejos siguen con `activationToken`.
  const invitado = user.invitePending === true || !!user.activationToken;
  const tokenExp =
    toDate(user.activationTokenExpiresAt) || toDate(user.activationTokenExpires);
  if (invitado && tokenExp) {
    return tokenExp > now
      ? ACCOUNT_STATES.INVITED_ACTIVE
      : ACCOUNT_STATES.INVITED_EXPIRED;
  }
  if (invitado && !tokenExp) {
    // Invitación sin TTL — anomalía. Tratamos como vencida por seguridad.
    return ACCOUNT_STATES.INVITED_EXPIRED;
  }

  // Sin token y sin activación: created (existe pero nunca se invitó).
  return ACCOUNT_STATES.CREATED;
}

/**
 * Labels legibles para cada estado (uso en UI).
 */
export const ACCOUNT_STATE_LABELS = Object.freeze({
  archived: "Archivada",
  active: "Activa",
  invited_active: "Invitada",
  invited_expired: "Vencida",
  created: "Sin enviar",
});

/**
 * Descripción más larga para tooltips / detalle.
 */
export const ACCOUNT_STATE_DESCRIPTIONS = Object.freeze({
  archived: "El admin archivó esta cuenta. No aparece en listados activos.",
  active: "El residente activó su cuenta y puede iniciar sesión.",
  invited_active: "Invitación enviada, esperando activación.",
  invited_expired: "El link de activación venció. Renová y reenviá.",
  created: "Existe en el sistema pero todavía no se le envió invitación.",
});

/**
 * Keys de StatusBadge para cada estado (usadas en statusColors.js).
 */
export const ACCOUNT_STATE_BADGE_KEYS = Object.freeze({
  archived: "cuenta_archivada",
  active: "cuenta_activa",
  invited_active: "cuenta_invitada",
  invited_expired: "cuenta_vencida",
  created: "cuenta_sin_enviar",
});

/**
 * Conjunto de estados que el admin "puede invitar" (acción masiva).
 */
export const STATES_INVITABLE = Object.freeze(new Set(["created"]));

/**
 * Estados sobre los que la acción "renovar y reenviar" tiene sentido.
 */
export const STATES_RENOVABLE = Object.freeze(
  new Set(["invited_active", "invited_expired"])
);

/**
 * ¿La cuenta puede iniciar sesión hoy?
 * @param {AccountStateUser} user
 * @param {Date} [now] - Fecha de referencia (default: Date.now). Útil para tests.
 * @returns {boolean}
 */
export function canLogin(user, now = new Date()) {
  return computeAccountState(user, now) === ACCOUNT_STATES.ACTIVE;
}

/**
 * ¿La cuenta tiene una invitación activa (no vencida)?
 * @param {AccountStateUser} user
 * @param {Date} [now] - Fecha de referencia (default: Date.now). Útil para tests.
 * @returns {boolean}
 */
export function hasActiveInvitation(user, now = new Date()) {
  return computeAccountState(user, now) === ACCOUNT_STATES.INVITED_ACTIVE;
}

export { ACCOUNT_STATES };
